import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { getUrlParam } from 'core-components/helpers/url'
import { PLANNING_METRIC_URL_SEGMENT } from 'partner360/configs'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { useActualsTypes } from './useActualsTypes'

const normalizeActualsTypeValue = (value?: string | null) => value?.trim().toLowerCase() || null

const selectableActualsTypes = Object.values(ActualsValueTypes).filter(
  (type): type is Exclude<ActualsValueTypes, ActualsValueTypes.None> =>
    type !== ActualsValueTypes.None
)

export const resolveRouteActualsType = ({
  routeActualsType,
  availableActualsTypes,
}: {
  routeActualsType?: string | null
  availableActualsTypes?: ActualsValueTypes[]
}) => {
  const normalizedRouteActualsType = normalizeActualsTypeValue(routeActualsType)

  if (!normalizedRouteActualsType) {
    return null
  }

  const resolvedRouteActualsType = selectableActualsTypes.find(
    (type) => normalizeActualsTypeValue(type) === normalizedRouteActualsType
  )

  if (!resolvedRouteActualsType) {
    return null
  }

  return availableActualsTypes?.includes(resolvedRouteActualsType) ? resolvedRouteActualsType : null
}

export const useRouteActualsTypeSync = () => {
  const location = useLocation()
  const { actualsType, actualsTypes, onChangeActualsType } = useActualsTypes()

  const routeActualsType = useMemo(
    () =>
      resolveRouteActualsType({
        routeActualsType: getUrlParam(PLANNING_METRIC_URL_SEGMENT, location.pathname),
        availableActualsTypes: actualsTypes,
      }),
    [actualsTypes, location.pathname]
  )

  useEffect(() => {
    if (!routeActualsType || routeActualsType === actualsType) {
      return
    }

    onChangeActualsType(routeActualsType)
  }, [actualsType, onChangeActualsType, routeActualsType])

  return routeActualsType
}
